import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDeptName, setNewDeptName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [message, setMessage] = useState('');

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/departments');
      setDepartments(res.data);
    } catch (err) {
      setMessage('Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    try {
      await api.post('/departments', { name: newDeptName });
      setNewDeptName('');
      setMessage('Department added successfully!');
      fetchDepartments();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to add department');
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/departments/${id}`, { name: editName });
      setEditingId(null);
      setMessage('Department updated successfully!');
      fetchDepartments();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to update department');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department? Employees assigned to this department might lose their department association.')) return;
    try {
      await api.delete(`/departments/${id}`);
      setMessage('Department deleted successfully!');
      fetchDepartments();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to delete department');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-8">
      <h2 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-4">Manage Departments</h2>
      
      {message && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md text-sm">
          {message}
        </div>
      )}

      <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2 mb-6">
        <input
          type="text"
          placeholder="New Department Name..."
          value={newDeptName}
          onChange={(e) => setNewDeptName(e.target.value)}
          className="flex-1 p-2 border rounded focus:ring-blue-500 focus:border-blue-500 text-sm"
        />
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium text-sm transition-colors w-full sm:w-auto"
        >
          Add Department
        </button>
      </form>

      {loading ? (
        <p className="text-gray-500 text-sm">Loading departments...</p>
      ) : departments.length === 0 ? (
        <p className="text-gray-500 text-sm">No departments found. Add one above.</p>
      ) : (
        <div className="space-y-2">
          {departments.map(dept => (
            <div key={dept._id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-md">
              {editingId === dept._id ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 p-1.5 border rounded mr-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              ) : (
                <span className="font-medium text-gray-800 text-sm">{dept.name}</span>
              )}
              
              <div className="flex items-center gap-2">
                {editingId === dept._id ? (
                  <>
                    <button onClick={() => handleUpdate(dept._id)} className="text-green-600 hover:text-green-700 text-sm font-medium">Save</button>
                    <button onClick={() => setEditingId(null)} className="text-gray-500 hover:text-gray-700 text-sm font-medium">Cancel</button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => {
                        setEditingId(dept._id);
                        setEditName(dept.name);
                      }} 
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(dept._id)} 
                      className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Departments;
